import {
  BOSS_POWER_DROPS,
  DeviceState,
  DifficultyId,
  EnemyState,
  EnemyType,
  GameMode,
  Owner,
  PickupKind,
  POWER_DROP_KILL_INTERVAL,
  POWER_OVERFLOW_SCORE,
  POWER_THRESHOLDS,
  SYNC_GATE_SURGE_VALUE,
  SYNC_GRADE_THRESHOLDS,
  SYNC_POWER_PICKUP_VALUE,
  InputAction,
  ProjectileFlags,
  ProjectileKind,
  WEAPON_MAX_LEVEL,
} from '../constants';
import { BossSystem } from '../bosses/BossSystem';
import { CollisionWorld } from '../collision/CollisionWorld';
import { getDifficulty, type DifficultySpec } from '../data/difficulty';
import { isEquipmentPurchase, PURCHASES, tryPurchase } from '../data/purchases';
import { getEnemyArchetype } from '../data/enemies';
import { EnemySystem } from '../enemies/EnemySystem';
import { PrismNodes } from '../formation/PrismNodes';
import type { InputManager } from '../input/InputManager';
import { DeterministicRandom } from '../math/DeterministicRandom';
import { BossPartPool } from '../pools/BossPartPool';
import { EnemyPool } from '../pools/EnemyPool';
import { ParticlePool } from '../pools/ParticlePool';
import { PickupPool } from '../pools/PickupPool';
import { ProjectilePool } from '../pools/ProjectilePool';
import { PlayerState } from '../player/PlayerState';
import { StageDirector } from '../stage/StageDirector';
import { PlayerSystem } from '../systems/PlayerSystem';
import { WeaponSystem } from '../weapons/WeaponSystem';
import { CoreBeamId, DeviceId, DEVICES, NODE_LENSES, NodeLensId } from '../weapons/definitions';

/** ?god=1 の開始 lux。ゲートの消費購入を全て試せる額で、進行そのものは変えない。 */
const GOD_DEBUG_LUX = 9999;

export type SfxSink = (id: number) => void;
export type WorldEventSink = (event: WorldEvent) => void;

export const CHAIN_WINDOW_TICKS = 180;

export const enum WorldEvent {
  StageStarted = 0,
  GateOpened = 1,
  GateClosed = 2,
  StageCleared = 3,
  PlayerDestroyed = 4,
  BossDefeated = 5,
  EndingReached = 6,
  WeaponLevelUp = 7,
}

export class GameWorld {
  readonly rng = new DeterministicRandom(0x4c554d49);
  readonly player = new PlayerState();
  readonly nodes = new PrismNodes();
  readonly projectiles = new ProjectilePool();
  readonly enemies = new EnemyPool();
  readonly particles = new ParticlePool();
  readonly pickups = new PickupPool();
  readonly bossParts = new BossPartPool();
  readonly stageDirector = new StageDirector();
  readonly playerSystem = new PlayerSystem();
  readonly enemySystem = new EnemySystem();
  readonly weapons = new WeaponSystem();
  readonly collisions = new CollisionWorld();
  readonly bosses = new BossSystem();

  mode = GameMode.Title;
  difficulty = DifficultyId.Bright;
  difficultySpec: DifficultySpec = getDifficulty(DifficultyId.Bright);
  deviceState = DeviceState.Ready;
  simTick = 0;
  runTick = 0;
  scrollX = 0;
  musicLayer = 0;
  hitStopTicks = 0;
  screenShakeTicks = 0;
  globalFlashTicks = 0;
  stageBonus = 0;
  stageScore = 0;
  defeatedEnemies = 0;
  continuesUsed = 0;
  currentStage = 0;
  lastPurchaseSucceeded = false;
  gateSelection = 0;
  gateMessageTicks = 0;
  /**
   * Default R2 progression mode. GameRuntime injects the route decision once
   * during init; false remains the safe default so ?r2=0 keeps the legacy shop.
   */
  continuousArsenalEnabled = false;
  /** ?god=1 のときだけ true。被弾を無効化するデバッグ用（GameRuntime.init で注入）。 */
  godMode = false;
  private stageStartScore = 0;
  private finalStageResultEnabled = false;
  private attackCounter = 1;
  private sfxSink: SfxSink = () => undefined;
  private worldEventSink: WorldEventSink = () => undefined;

  setSfxSink(sink: SfxSink): void {
    this.sfxSink = sink;
  }

  setWorldEventSink(sink: WorldEventSink): void {
    this.worldEventSink = sink;
  }

  /** R2だけ最終ステージにも評価画面を挟む。既定falseでlegacy遷移を維持する。 */
  setFinalStageResultEnabled(enabled: boolean): void {
    this.finalStageResultEnabled = enabled;
  }

  setContinuousArsenalEnabled(enabled: boolean): void {
    this.continuousArsenalEnabled = enabled;
  }

  emitSfx(id: number): void {
    this.sfxSink(id);
  }

  emitEvent(event: WorldEvent): void {
    this.worldEventSink(event);
  }

  /** Dying中の18tickを余韻として含む、割当ゼロの共有戦闘圧判定。 */
  hasCombatPressure(): boolean {
    return this.enemies.indices.count > 0 || this.bosses.active;
  }

  /**
   * ?god=1 の武器デバッグ土台。装備3系統を全て所持済みにし、武器段位を最大で開始する。
   * 段位はドロップ待ちの 5 体撃破 × 4 段、装備はゲート到達 + lux を待たないと揃わないため、
   * 全強化形態の検証が実質ステージ後半以降に固定されてしまうのを外す。
   * 所持済みにするのは装備（コア/レンズ/デバイス）だけで、耐久・ノード・スコア系の
   * 消費購入はゲートの購入経路そのものを検証対象に残すため lux で賄う。
   * 無指定時（godMode = false）は何も書き換えない。
   */
  private applyGodArsenal(): void {
    if (!this.godMode) return;
    const player = this.player;
    player.weaponLevel = WEAPON_MAX_LEVEL;
    player.powerCharge = 0;
    player.lux = GOD_DEBUG_LUX;
    for (const item of PURCHASES) {
      if (isEquipmentPurchase(item)) player.purchaseCounts[item.id] = 1;
    }
  }

  /** ?god=1 専用: コアビームを直接指定する。無指定時は何もしない。 */
  setGodCore(core: CoreBeamId): void {
    if (!this.godMode) return;
    this.player.core = core;
  }

  /** ?god=1 専用: ノードレンズを巡回する。無指定時は何もしない。 */
  cycleGodLens(): void {
    if (!this.godMode) return;
    this.player.lens = ((this.player.lens + 1) % NODE_LENSES.length) as NodeLensId;
  }

  /** ?god=1 専用: デバイスを巡回する。装備直後は即使用できるよう冷却を落とす。 */
  cycleGodDevice(): void {
    if (!this.godMode) return;
    this.player.device = ((this.player.device + 1) % DEVICES.length) as DeviceId;
    this.player.deviceCooldown = 0;
    this.player.deviceActiveTicks = 0;
  }

  /** ?god=1 専用: 武器段位を 0..MAX で巡回する。低段位のパターン確認用。 */
  cycleGodWeaponLevel(): void {
    if (!this.godMode) return;
    this.player.weaponLevel = (this.player.weaponLevel + 1) % (WEAPON_MAX_LEVEL + 1);
    this.player.powerCharge = 0;
  }

  startNewGame(difficulty: DifficultyId): void {
    this.difficulty = difficulty;
    this.difficultySpec = getDifficulty(difficulty);
    this.player.resetForNewGame(difficulty);
    this.continuesUsed = 0;
    this.defeatedEnemies = 0;
    this.runTick = 0;
    this.attackCounter = 1;
    this.rng.reset(0x4c554d49 ^ ((difficulty + 1) * 0x9e3779b9));
    this.applyGodArsenal();
    this.startStage(0, true);
  }

  startStage(stageId: number, preserveInventory = true): void {
    this.currentStage = Math.max(0, Math.min(3, stageId | 0));
    if (!preserveInventory) {
      this.player.resetForNewGame(this.difficulty);
      this.applyGodArsenal();
    } else {
      this.player.resetForStage();
    }
    this.nodes.reset(this.player);
    this.projectiles.reset();
    this.enemies.reset();
    this.particles.reset();
    this.pickups.reset();
    this.bosses.reset(this);
    this.stageDirector.start(this.currentStage);
    this.mode = GameMode.Playing;
    this.simTick = 0;
    this.scrollX = 0;
    this.musicLayer = 0;
    this.hitStopTicks = 0;
    this.screenShakeTicks = 0;
    this.globalFlashTicks = 0;
    this.stageBonus = 0;
    this.stageScore = 0;
    this.stageStartScore = this.player.score;
    this.deviceState = this.player.deviceCooldown > 0 ? DeviceState.Cooldown : DeviceState.Ready;
    this.emitEvent(WorldEvent.StageStarted);
  }

  update(input: InputManager): void {
    // HOTPATH-BEGIN
    if (this.mode === GameMode.CalibrationGate) {
      this.updateCalibrationGate(input);
      return;
    }
    if (this.mode !== GameMode.Playing) return;
    if (this.hitStopTicks > 0) {
      this.hitStopTicks -= 1;
      return;
    }
    this.simTick += 1;
    this.runTick += 1;
    if (this.screenShakeTicks > 0) this.screenShakeTicks -= 1;
    if (this.globalFlashTicks > 0) this.globalFlashTicks -= 1;
    this.playerSystem.update(this, input);
    if (this.mode !== GameMode.Playing) return;
    const repairActive = this.player.device === DeviceId.RepairField && this.player.deviceActiveTicks > 0;
    this.nodes.update(this.player, repairActive);
    this.stageDirector.update(this);
    if (this.mode !== GameMode.Playing) return;
    this.enemySystem.update(this);
    this.bosses.update(this);
    this.weapons.updateProjectiles(this);
    this.projectiles.update(this.enemyProjectileTimeScale());
    this.pickups.update();
    this.particles.update();
    this.collisions.update(this);
    if (this.player.deviceCooldown <= 0 && this.player.deviceActiveTicks <= 0) this.deviceState = DeviceState.Ready;
    else if (this.player.deviceActiveTicks <= 0) this.deviceState = DeviceState.Cooldown;
    // HOTPATH-END
  }

  nextAttackId(): number {
    this.attackCounter = (this.attackCounter + 1) >>> 0;
    if (this.attackCounter === 0) this.attackCounter = 1;
    return this.attackCounter;
  }

  spawnWave(type: EnemyType, count: number, pattern: number): void {
    const archetype = getEnemyArchetype(type);
    const limited = Math.max(1, Math.min(24, count | 0));
    for (let i = 0; i < limited; i += 1) {
      let y = 30 + ((i * 37 + pattern * 19) % 156);
      const inverseFlow = this.currentStage === 2 && pattern >= 9 && pattern <= 11;
      let x = inverseFlow
        ? -20 - i * (type >= EnemyType.ChoirEngine ? 30 : 18)
        : 404 + i * (type >= EnemyType.ChoirEngine ? 30 : 18);
      if ((pattern & 3) === 1) y = 24 + (i % 2) * 168;
      else if ((pattern & 3) === 2) y = 108 + Math.sin((i / Math.max(1, limited - 1)) * Math.PI * 2) * 72;
      else if ((pattern & 3) === 3) {
        const row = i % 3;
        y = 56 + row * 52;
        x += Math.floor(i / 3) * 18;
      }
      this.enemySystem.spawn(this, type, x, y, pattern, i & 1);
    }
  }

  destroyEnemy(index: number): void {
    if (this.enemies.indices.active[index] === 0 || this.enemies.state[index] === EnemyState.Dying) return;
    const x = this.enemies.x[index];
    const y = this.enemies.y[index];
    const baseScore = this.enemies.score[index];
    const lux = Math.max(1, Math.round(this.enemies.lux[index] * this.difficultySpec.luxScale));
    const chainMultiplier = 1 + Math.min(20, this.player.chain) * 0.05;
    const scoreMultiplier = this.player.scoreBoostTicks > 0 ? 2 : 1;
    this.player.score += Math.round(baseScore * chainMultiplier * scoreMultiplier);
    this.player.chain = Math.min(99, this.player.chain + 1);
    this.player.chainTimer = CHAIN_WINDOW_TICKS;
    this.defeatedEnemies += 1;
    this.stageBonus += baseScore;
    this.enemies.state[index] = EnemyState.Dying;
    this.enemies.stateTimer[index] = 18;
    if (this.continuousArsenalEnabled) this.grantLux(lux);
    else this.pickups.spawn(PickupKind.Lux, x, y, lux, -0.55, (this.rng.nextFloat() - 0.5) * 0.6);
    if (this.defeatedEnemies % 37 === 0 && this.player.lives < this.player.maxLives) this.pickups.spawn(PickupKind.Repair, x, y - 7, 1, -0.42, -0.18);
    if (this.defeatedEnemies % POWER_DROP_KILL_INTERVAL === 0) {
      if (this.continuousArsenalEnabled) this.grantPower(1);
      else this.pickups.spawn(PickupKind.Power, x, y - 4, 1, -0.5, -0.24);
    }
    this.particles.burst(this.rng, x, y, this.enemies.type[index] >= EnemyType.ChoirEngine ? 18 : 9, 2.4, 34, 2);
    this.hitStopTicks = Math.max(this.hitStopTicks, this.enemies.type[index] >= EnemyType.ChoirEngine ? 3 : 1);
    this.screenShakeTicks = Math.max(this.screenShakeTicks, this.enemies.type[index] >= EnemyType.ChoirEngine ? 8 : 3);
    this.emitSfx(this.enemies.type[index] >= EnemyType.ChoirEngine ? 4 : 2);
  }

  spawnPrismRefraction(sourceEnemy: number, projectile: number): void {
    const enemies = this.enemies;
    let target = -1;
    let best = 130 * 130;
    for (let i = 0; i < enemies.indices.active.length; i += 1) {
      if (i === sourceEnemy || enemies.indices.active[i] === 0 || enemies.state[i] === EnemyState.Dying) continue;
      const dx = enemies.x[i] - this.projectiles.x[projectile];
      const dy = enemies.y[i] - this.projectiles.y[projectile];
      const distanceSq = dx * dx + dy * dy;
      if (distanceSq < best) {
        best = distanceSq;
        target = i;
      }
    }
    if (target < 0) return;
    const dx = enemies.x[target] - this.projectiles.x[projectile];
    const dy = enemies.y[target] - this.projectiles.y[projectile];
    const inv = best > 0.001 ? 1 / Math.sqrt(best) : 1;
    const speed = 5.2;
    this.projectiles.spawn(
      this.projectiles.x[projectile],
      this.projectiles.y[projectile],
      dx * inv * speed,
      dy * inv * speed,
      2,
      46,
      this.projectiles.damage[projectile] * 0.62,
      Owner.Player,
      ProjectileKind.Prism,
      ProjectileFlags.None,
      this.nextAttackId(),
      0,
      this.projectiles.chainDepth[projectile] + 1,
    );
    this.emitSfx(6);
  }

  damagePlayer(): void {
    if (this.godMode) return;
    const player = this.player;
    if (!player.alive || player.invulnerabilityTicks > 0) return;
    player.alive = false;
    player.lives -= 1;
    player.chain = 0;
    player.chainTimer = 0;
    if (!this.continuousArsenalEnabled) {
      player.weaponLevel = Math.max(0, player.weaponLevel - 1);
      player.powerCharge = 0;
    }
    this.projectiles.clearOwner(Owner.Enemy);
    this.particles.burst(this.rng, player.x, player.y, 26, 3, 52, 4);
    this.hitStopTicks = 10;
    this.screenShakeTicks = 18;
    this.globalFlashTicks = 8;
    this.emitSfx(15);
    this.emitEvent(WorldEvent.PlayerDestroyed);
    if (player.lives > 0) player.respawnTicks = 90;
    else this.mode = GameMode.GameOver;
  }

  enterCalibrationGate(): void {
    this.mode = GameMode.CalibrationGate;
    this.projectiles.clearOwner(Owner.Enemy);
    this.lastPurchaseSucceeded = false;
    this.gateSelection = 0;
    this.gateMessageTicks = 0;
    this.emitSfx(16);
    this.emitEvent(WorldEvent.GateOpened);
  }

  purchase(itemId: number): boolean {
    if (this.mode !== GameMode.CalibrationGate) return false;
    const result = tryPurchase(this.player, itemId, this.difficulty);
    this.lastPurchaseSucceeded = result;
    this.gateMessageTicks = 75;
    this.emitSfx(result ? 17 : 18);
    return result;
  }

  leaveCalibrationGate(): void {
    if (this.mode !== GameMode.CalibrationGate) return;
    this.mode = GameMode.Playing;
    this.hitStopTicks = 0;
    this.emitEvent(WorldEvent.GateClosed);
  }

  /**
   * R2 continuous-arsenal replacement for the modal shop. The StageDirector
   * keeps consuming the same Gate cue, but play never leaves GameMode.Playing.
   */
  triggerContinuousGateSurge(): void {
    if (!this.continuousArsenalEnabled || this.mode !== GameMode.Playing) return;
    this.grantSyncCharge(SYNC_GATE_SURGE_VALUE, true);
    this.globalFlashTicks = Math.max(this.globalFlashTicks, 5);
    this.screenShakeTicks = Math.max(this.screenShakeTicks, 8);
    this.emitSfx(16);
  }

  finishStage(): void {
    this.projectiles.clearOwner(Owner.Enemy);
    if (this.currentStage >= 3 && !this.finalStageResultEnabled) {
      this.mode = GameMode.Ending;
      this.emitEvent(WorldEvent.EndingReached);
      return;
    }
    const lifeBonus = this.player.lives * 2500;
    const luxBonus = this.player.lux * 20;
    this.stageBonus += lifeBonus + luxBonus;
    this.player.score += lifeBonus + luxBonus;
    this.stageScore = Math.max(0, this.player.score - this.stageStartScore);
    this.mode = GameMode.StageResult;
    this.emitEvent(WorldEvent.StageCleared);
  }

  advanceStage(): void {
    if (this.mode !== GameMode.StageResult) return;
    if (this.currentStage >= 3) {
      this.mode = GameMode.Ending;
      this.emitEvent(WorldEvent.EndingReached);
      return;
    }
    this.startStage(this.currentStage + 1, true);
  }

  continueRun(): void {
    if (this.mode !== GameMode.GameOver) return;
    this.continuesUsed += 1;
    this.player.continueRun(this.difficultySpec.continueLives);
    this.startStage(this.currentStage, true);
  }

  retryStage(): void {
    this.player.lives = this.player.maxLives;
    this.player.lux = Math.floor(this.player.lux * 0.75);
    this.startStage(this.currentStage, true);
  }

  returnToTitle(): void {
    this.projectiles.reset();
    this.enemies.reset();
    this.particles.reset();
    this.pickups.reset();
    this.bosses.reset(this);
    this.mode = GameMode.Title;
    this.musicLayer = 0;
  }

  resume(): void {
    if (this.mode === GameMode.Paused) this.mode = GameMode.Playing;
  }

  setGateSelection(itemId: number): void {
    this.gateSelection = Math.max(0, Math.min(15, itemId | 0));
  }

  private updateCalibrationGate(input: InputManager): void {
    if (this.gateMessageTicks > 0) this.gateMessageTicks -= 1;
    if (input.pressed[InputAction.Up] !== 0) this.gateSelection = (this.gateSelection + 15) & 15;
    if (input.pressed[InputAction.Down] !== 0) this.gateSelection = (this.gateSelection + 1) & 15;
    if (input.pressed[InputAction.Left] !== 0) this.gateSelection = (this.gateSelection + 12) & 15;
    if (input.pressed[InputAction.Right] !== 0) this.gateSelection = (this.gateSelection + 4) & 15;
    if (input.pressed[InputAction.Confirm] !== 0 || input.pressed[InputAction.Fire] !== 0) this.purchase(this.gateSelection);
    if (input.pressed[InputAction.Cancel] !== 0 || input.pressed[InputAction.Pause] !== 0) this.leaveCalibrationGate();
  }

  nodeDefenseDisabled(): boolean {
    return this.player.device === DeviceId.Overdrive && this.player.deviceActiveTicks > 0;
  }

  playerDamageScale(): number {
    return this.player.device === DeviceId.Overdrive && this.player.deviceActiveTicks > 0 ? 1.75 : 1;
  }

  enemyProjectileTimeScale(): number {
    return this.player.device === DeviceId.TimeFold && this.player.deviceActiveTicks > 0 ? 0.42 : 1;
  }

  onBossDefeated(): void {
    this.player.score += this.bosses.maxHp * 10;
    this.grantLux(Math.round(40 * this.difficultySpec.luxScale));
    // Stage-end flow freezes pickups before they can be collected, so the
    // boss's power reward is granted directly rather than spawned as pickups.
    this.grantPower(BOSS_POWER_DROPS);
    this.emitEvent(WorldEvent.BossDefeated);
  }

  /** Grants `count` units of weapon power, one at a time (each may cross a level threshold). */
  grantPower(count: number): void {
    if (this.continuousArsenalEnabled) {
      this.grantSyncCharge(count * SYNC_POWER_PICKUP_VALUE, true);
      return;
    }
    const player = this.player;
    for (let i = 0; i < count; i += 1) {
      if (player.weaponLevel >= WEAPON_MAX_LEVEL) {
        player.score += POWER_OVERFLOW_SCORE;
        continue;
      }
      player.powerCharge += 1;
      if (player.powerCharge >= POWER_THRESHOLDS[player.weaponLevel]) {
        player.powerCharge = 0;
        player.weaponLevel += 1;
        this.emitEvent(WorldEvent.WeaponLevelUp);
        this.emitSfx(19);
      }
    }
  }

  /**
   * Lux remains the run's archive value. The R2 route banks defeated-enemy Lux
   * here immediately; legacy reaches the same method only after pickup contact.
   */
  grantLux(value: number): void {
    const amount = Math.max(0, value | 0);
    this.player.lux += amount;
    if (this.continuousArsenalEnabled) this.grantSyncCharge(amount, false);
  }

  private grantSyncCharge(value: number, rewardOverflow: boolean): void {
    const player = this.player;
    let remaining = Math.max(0, value | 0);
    while (remaining > 0) {
      if (player.weaponLevel >= WEAPON_MAX_LEVEL) {
        if (rewardOverflow) {
          player.score += Math.round((remaining / SYNC_POWER_PICKUP_VALUE) * POWER_OVERFLOW_SCORE);
        }
        return;
      }
      const threshold = SYNC_GRADE_THRESHOLDS[player.weaponLevel] ?? 1;
      const needed = Math.max(1, threshold - player.powerCharge);
      const applied = Math.min(needed, remaining);
      player.powerCharge += applied;
      remaining -= applied;
      if (player.powerCharge < threshold) continue;
      player.powerCharge = 0;
      player.weaponLevel += 1;
      this.emitEvent(WorldEvent.WeaponLevelUp);
      this.emitSfx(19);
    }
  }
}
